/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import { COST_ITEM_DEPRECIATION_SCHEDULES, CostItemBasis, CostItemDirection, CostItemScope, costItemScheduleKinds, ValidDepreciationValues, } from './fimo-enums';
/**
 * Long-format cost item import, shared by the browser parser and the server-built Excel template.
 * One row per period; rows sharing a name form one item. Each row says when it applies in exactly
 * one period column, chosen by its `schedule_kind` (see `COST_ITEM_PERIOD_COLUMNS`).
 */
export const COST_ITEM_IMPORT_COLUMNS = [
    'name',
    'group',
    'treatment',
    'depreciation_schedule',
    'direction',
    'scope',
    'basis',
    'value',
    'schedule_kind',
    'start_operating_year',
    'end_operating_year',
    'escalator',
    'operating_year',
    'calendar_year',
    'month',
    'payment_date',
];
const IMPORT_COLUMN_NAMES = new Set(COST_ITEM_IMPORT_COLUMNS);
/**
 * Whether a row of cells is a header: at least two are column names. Without one, columns follow
 * `COST_ITEM_IMPORT_COLUMNS` (rows copied from the template).
 */
export const isCostItemImportHeader = (cells) => cells.filter((cell) => 
// CSV exports may quote every header cell.
IMPORT_COLUMN_NAMES.has(cell
    .trim()
    .replace(/^"(.*)"$/, '$1')
    .trim()
    .toLowerCase())).length >= 2;
/** The period columns a row of each schedule kind may use (annual series: one of the two). */
export const COST_ITEM_PERIOD_COLUMNS = {
    flat_escalating: ['start_operating_year'],
    annual_series: ['operating_year', 'calendar_year'],
    monthly_series: ['month'],
    dated_payments: ['payment_date'],
};
/** Columns only flat escalating rows use, besides their period column. */
const FLAT_ONLY_COLUMNS = [
    'end_operating_year',
    'escalator',
];
/** Schedule kinds that use each schedule column; rows of every other kind leave it blank. */
export const COST_ITEM_COLUMN_KINDS = (() => {
    const kinds = new Map();
    for (const kind of costItemScheduleKinds) {
        for (const column of COST_ITEM_PERIOD_COLUMNS[kind]) {
            kinds.set(column, [...(kinds.get(column) ?? []), kind]);
        }
    }
    for (const column of FLAT_ONLY_COLUMNS)
        kinds.set(column, ['flat_escalating']);
    return kinds;
})();
/** Four-digit calendar years, for annual series by calendar year. */
export const CALENDAR_YEAR_LIMITS = { min: 1000, max: 9999 };
/** Values used when an optional column is left blank (and the item dialog's starting values). */
export const COST_ITEM_IMPORT_DEFAULTS = {
    treatment: 'opex',
    depreciation_schedule: ValidDepreciationValues.FIVE_YEAR_MACRS,
    direction: CostItemDirection.COST,
    scope: CostItemScope.PROJECT,
    basis: CostItemBasis.USD,
    escalator: '0',
};
/** Values accepted by the columns that take a fixed list (Excel template dropdowns). */
export const COST_ITEM_IMPORT_CHOICES = {
    treatment: ['opex', 'capital'],
    depreciation_schedule: COST_ITEM_DEPRECIATION_SCHEDULES,
    direction: Object.values(CostItemDirection),
    scope: Object.values(CostItemScope),
    basis: Object.values(CostItemBasis),
    schedule_kind: [...costItemScheduleKinds],
};
/** What each column means, for the template's read-me sheet. */
export const COST_ITEM_IMPORT_HELP = {
    name: 'Item name. Rows with the same name form one item.',
    group: 'Optional reporting group, e.g. Community & donations or Miscellaneous. Results show one row per group; items left blank are combined into one Other cost items row (or a single Custom Cost Items row when no item has a group). Any text works; the dropdown only suggests.',
    treatment: `opex (expensed) or capital (capitalized and depreciated). Default ${COST_ITEM_IMPORT_DEFAULTS.treatment}.`,
    depreciation_schedule: `Capital items only. Default ${COST_ITEM_IMPORT_DEFAULTS.depreciation_schedule}. Capital paid after COD needs MACRS or straight-line.`,
    direction: `cost, or credit for money received (e.g. a utility reimbursement or grant). Default ${COST_ITEM_IMPORT_DEFAULTS.direction}. A capital credit must fall on or before COD; after COD enter money received as an OPEX credit.`,
    scope: `project, pv or ess. Must be pv for $/kW and $/MWh units, ess for $/kWh. Default ${COST_ITEM_IMPORT_DEFAULTS.scope}.`,
    basis: `Unit of value: usd, usd_per_kw_dc, usd_per_kw_ac, usd_per_kwh_ess (installed energy) or usd_per_mwh_net_gen. Default ${COST_ITEM_IMPORT_DEFAULTS.basis}.`,
    schedule_kind: 'flat_escalating (one row), annual_series (one row per year), monthly_series (one row per month) or dated_payments (one row per payment).',
    start_operating_year: 'flat_escalating only: first operating year the cost applies (1 = first year after COD). That year is charged value unescalated.',
    end_operating_year: 'flat_escalating only: last operating year the cost applies, included. Blank runs to the end of the term.',
    operating_year: "annual_series by operating year: the year this row's value applies to (1 = first year after COD). One row per year, no gaps.",
    calendar_year: 'annual_series by calendar year: the year, e.g. 2031. Partial years are prorated.',
    month: 'monthly_series: the month, e.g. 2031-01.',
    payment_date: 'dated_payments: the payment date, e.g. 2027-03-31. Payments are dated at month end.',
    value: 'Amount in the basis unit, never negative (use direction = credit). flat_escalating: first-year value.',
    escalator: `flat_escalating only: annual escalation in percent from 0 to 50, e.g. 2 for 2%. Default ${COST_ITEM_IMPORT_DEFAULTS.escalator}.`,
};
/** Example rows for the template, one or more per schedule kind; users replace them. */
export const COST_ITEM_TEMPLATE_ROWS = [
    {
        name: 'Example - right-of-way rent',
        group: 'Land & right-of-way',
        schedule_kind: 'flat_escalating',
        start_operating_year: '1',
        value: '24000',
        escalator: '2',
    },
    {
        name: 'Example - state lease fee',
        group: 'Land & right-of-way',
        scope: 'pv',
        basis: 'usd_per_kw_ac',
        schedule_kind: 'annual_series',
        operating_year: '1',
        value: '5',
    },
    {
        name: 'Example - state lease fee',
        group: 'Land & right-of-way',
        scope: 'pv',
        basis: 'usd_per_kw_ac',
        schedule_kind: 'annual_series',
        operating_year: '2',
        value: '5.5',
    },
    {
        name: 'Example - county road agreement',
        group: 'Roads & local infrastructure',
        schedule_kind: 'annual_series',
        calendar_year: '2031',
        value: '12000',
    },
    {
        name: 'Example - BESS monitoring',
        group: 'O&M & monitoring',
        scope: 'ess',
        basis: 'usd_per_kwh_ess',
        schedule_kind: 'monthly_series',
        month: '2031-01',
        value: '0.05',
    },
    {
        name: 'Example - BESS monitoring',
        group: 'O&M & monitoring',
        scope: 'ess',
        basis: 'usd_per_kwh_ess',
        schedule_kind: 'monthly_series',
        month: '2031-02',
        value: '0.05',
    },
    {
        name: 'Example - contingency',
        group: 'Contingency',
        treatment: 'capital',
        depreciation_schedule: 'five_year_macrs',
        schedule_kind: 'dated_payments',
        payment_date: '2027-03-31',
        value: '400000',
    },
    {
        name: 'Example - contingency',
        group: 'Contingency',
        treatment: 'capital',
        depreciation_schedule: 'five_year_macrs',
        schedule_kind: 'dated_payments',
        payment_date: '2027-09-30',
        value: '250000',
    },
    {
        name: 'Example - utility reimbursement',
        group: 'Interconnection & utility',
        treatment: 'capital',
        depreciation_schedule: 'indirect',
        direction: 'credit',
        schedule_kind: 'dated_payments',
        payment_date: '2027-06-30',
        value: '100000',
    },
].map((example) => ({
    // Spell out the item-level defaults so the template shows every option a user can set.
    treatment: COST_ITEM_IMPORT_DEFAULTS.treatment,
    direction: COST_ITEM_IMPORT_DEFAULTS.direction,
    scope: COST_ITEM_IMPORT_DEFAULTS.scope,
    basis: COST_ITEM_IMPORT_DEFAULTS.basis,
    ...example,
}));
//# sourceMappingURL=fimo-cost-item-import.js.map